import { forwardRef, useImperativeHandle } from 'react';
import { MoreVertical } from 'lucide-react';
import Avatar from '../../../_components/ui/Avatar';
import UserDropdown from '../UserDropdown';
import useClickOutside from '../../../_hooks/useClickOutside';
import { useUserInfo } from '../hooks/useUserInfo';
import { useUserDropdown } from '../hooks/useUserDropdown';
import './NavigationFooter.scss';

export const NavigationFooter = forwardRef(({ onLogoutClick, mini, isSettingsMode }, ref) => {
  const { user, designation } = useUserInfo();
  const {
    triggerRef,
    toggleDropdown,
    closeDropdown,
    dropdownList,
    dropdownPosition,
    shouldShowDropdown,
  } = useUserDropdown(onLogoutClick);

  // The dropdown renders wider than the (56-240px) nav rail, so it visually
  // overflows the rail's own box - this is the real dismiss mechanism now,
  // not the rail's mouseleave (see useNavigationState.js for why that used
  // to close the dropdown out from under you).
  useClickOutside(triggerRef, closeDropdown);

  useImperativeHandle(
    ref,
    () => ({
      closeDropdown,
      isDropdownOpen: shouldShowDropdown,
    }),
    [closeDropdown, shouldShowDropdown]
  );

  const firstName = user?.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1) : '';
  const lastName = user?.lastName ? user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1) : '';
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    <div ref={triggerRef} className="nav-footer" onClick={toggleDropdown}>
      <div className="nav-footer__user">
        <Avatar
          src={user?.imageUrl}
          alt={fullName}
          width="32px"
          height="32px"
          orgName={fullName}
          id={user?._id}
          // The dark main rail uses a translucent white circle - on the
          // settings rail's light glass background that same overlay reads
          // as an almost-invisible ring around white-on-white text, so it
          // needs a solid, on-brand fill there instead.
          backgroundColor={isSettingsMode ? "var(--color-primary, #B4531A)" : "rgba(255, 255, 255, 0.1)"}
          // Avatar's shared default (navy-blue initials) is tuned for its
          // other callers' light pastel fallback palette - both of this
          // component's own backgrounds (a near-transparent white circle
          // over the dark main rail, and a solid primary-orange circle on
          // settings) read dark-on-dark/dark-on-saturated without an
          // explicit override.
          textColor="#FFFFFF"
        />
        <div className={`nav-footer__info ${mini ? "nav-footer__info--hidden" : ""}`}>
          <p className="nav-footer__name">{fullName}</p>
          <p className="nav-footer__role">{designation}</p>
        </div>
      </div>
      <div className={`nav-footer__actions ${mini ? "nav-footer__actions--hidden" : ""}`}>
        <MoreVertical size={16} />
      </div>
      {/* Rendered as a direct child of the footer, not inside __actions: that
          block is collapsed to width 0 with overflow hidden on the mini
          rail, which clipped the whole dropdown (org switcher, New
          organisation, Logout) whenever the sidebar was collapsed. */}
      {shouldShowDropdown && (
        <UserDropdown dropdownList={dropdownList} position={dropdownPosition} />
      )}
    </div>
  );
});

NavigationFooter.displayName = 'NavigationFooter';
